import { useState } from 'react';
import { useMe, useMembers } from '../../../api/queries';
import { useAddMember, useRemoveMember, useSetMemberRole } from '../../../api/mutations';
import { DangerButton } from '../../../components/DangerButton';
import { Field } from '../../../components/Field';
import { Ic } from '../../../components/Ic';
import { dateTime } from '../../../lib/time';
import type { Role } from '../../../api/types';

const ROLES: Array<Exclude<Role, ''>> = ['admin', 'viewer'];

export function MembersTab() {
  const { data: me } = useMe();
  const { data: members, error } = useMembers();
  const add = useAddMember();
  const setRole = useSetMemberRole();
  const remove = useRemoveMember();

  const [discordId, setDiscordId] = useState('');
  const [role, setNewRole] = useState<Exclude<Role, ''>>('viewer');

  if (error) {
    return (
      <div className="pcard">
        <div className="body pad">
          <div className="err">Members are visible to the super admin only.</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Surfacing this rather than papering over it: nothing in the codebase
          checks for 'admin', so the two roles currently grant the same access. */}
      <div className="pcard" style={{ borderColor: 'rgba(245,185,66,.3)' }}>
        <div className="body pad">
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Ic.alert style={{ width: 17, height: 17, color: 'var(--warn)', flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>
              <strong>admin and viewer are currently equivalent.</strong> Access checks only
              distinguish the super admin from everyone else, so both roles grant the same
              permissions today.
            </div>
          </div>
        </div>
      </div>

      <div className="pcard">
        <div className="head">
          <Ic.plus className="ic" />
          <h3>Add a member</h3>
        </div>
        <div className="body pad" style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <Field label="Discord user ID" style={{ flex: 1 }}>
            <input
              className="input mono-sm"
              placeholder="270151497663119360"
              value={discordId}
              onChange={(e) => setDiscordId(e.target.value.trim())}
            />
          </Field>
          <Field label="Role">
            <select
              className="input"
              value={role}
              onChange={(e) => setNewRole(e.target.value as Exclude<Role, ''>)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
          <button
            className="btn primary"
            disabled={!discordId || add.isPending}
            onClick={() =>
              add.mutate(
                { discord_id: discordId, role },
                { onSuccess: () => setDiscordId('') },
              )
            }
          >
            Add
          </button>
        </div>
      </div>

      <div className="pcard">
        <div className="head">
          <Ic.user className="ic" />
          <h3>Members</h3>
          <span className="spacer" />
          <span className="pill">{members?.length ?? 0}</span>
        </div>
        <div className="tbl-scroll">
          <table className="assign">
            <thead>
              <tr>
                <th>Member</th>
                <th>Discord ID</th>
                <th>Role</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members?.map((m) => {
                const isSelf = m.discord_id === me?.id;
                const isSuper = m.role === 'super_admin';
                return (
                  <tr key={m.discord_id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {m.avatar && (
                          <img src={m.avatar} alt="" width={20} height={20} style={{ borderRadius: '50%' }} />
                        )}
                        {m.name}
                        {isSelf && <span className="pill">you</span>}
                      </span>
                    </td>
                    <td className="mono-sm">{m.discord_id}</td>
                    <td>
                      {isSuper ? (
                        <span className="pill indigo">super admin</span>
                      ) : (
                        <select
                          className="input"
                          style={{ width: 'auto', padding: '4px 26px 4px 8px' }}
                          value={m.role}
                          onChange={(e) =>
                            setRole.mutate({
                              discord_id: m.discord_id,
                              role: e.target.value as Exclude<Role, ''>,
                            })
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="mono-sm">{m.added_at ? dateTime(m.added_at) : '—'}</td>
                    <td>
                      {!isSuper && (
                        <DangerButton onConfirm={() => remove.mutate(m)} confirmLabel="Remove?">
                          <Ic.trash className="ico" />
                        </DangerButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
